import { IconMinus, IconTick } from 'braid-design-system';

# Application signals

Application signals track the progression of SEEK candidates through the recruitment process.
Feeding these back to SEEK helps to improve our search & recommendation capabilities,
and ultimately match candidates with the optimal positions on offer.

Signal capture is required if the candidate used SEEK to apply for the position.
This includes:

- Discovering the position through a SEEK job ad
- Using SEEK’s Apply Form
- Using [Apply with SEEK] to pre-fill an external apply form

|       Advertised on SEEK       | Application source |  Application method   |    Using [Apply with SEEK]     |  Send application completes?   | Send application status updates? |
| :----------------------------: | :----------------: | :-------------------: | :----------------------------: | :----------------------------: | :------------------------------: |
|  <IconTick tone="positive" />  |    SEEK job ad     |  [SEEK’s Apply Form]  | <IconMinus tone="secondary" /> | <IconMinus tone="secondary" /> |   <IconTick tone="positive" />   |
|  <IconTick tone="positive" />  |    SEEK job ad     | [External apply form] |  <IconTick tone="positive" />  |  <IconTick tone="positive" />  |   <IconTick tone="positive" />   |
|  <IconTick tone="positive" />  |    SEEK job ad     |  External apply form  | <IconMinus tone="secondary" /> |  <IconTick tone="positive" />  |   <IconTick tone="positive" />   |
|  <IconTick tone="positive" />  |    Other source    |  External apply form  |  <IconTick tone="positive" />  |  <IconTick tone="positive" />  |   <IconTick tone="positive" />   |
|  <IconTick tone="positive" />  |    Other source    |  External apply form  | <IconMinus tone="secondary" /> | <IconMinus tone="secondary" /> |  <IconMinus tone="secondary" />  |
| <IconMinus tone="secondary" /> |    Other source    |  External apply form  |  <IconTick tone="positive" />  |  <IconTick tone="positive" />  |  <IconMinus tone="secondary" />  |
| <IconMinus tone="secondary" /> |    Other source    |  External apply form  | <IconMinus tone="secondary" /> | <IconMinus tone="secondary" /> |  <IconMinus tone="secondary" />  |

## Application complete for external applications

The first signal that we capture from the external recruitment process is the application complete signal.
Note that an application complete signal does _not_ imply a candidate source or Apply with SEEK usage;
[Ad Performance] analytics will only attribute completed applications where the candidate source is SEEK.

Pass the `seek-token` persisted from SEEK job ad or Apply with SEEK redirection as the [correlating identifier].

```graphql
mutation ($input: SendSignalInput!) {
  sendSignal(input: $input)
}
```

```jsonc default
{
  "input": {
    "typeCode": "ApplicationComplete",

    // The identifier to correlate the signal back to SEEK job ad or Apply with SEEK redirection.
    "token": "globalPublicTest-WfRFAhG25YC8ADe3QvxAVz",
    "timestamp": "2020-01-02T00:00:00.000Z"
  }
}
```

The `ApplicationComplete` signal represents a bespoke state—the completion of the application subprocess—so it does not require `current` and `previous` states to be supplied.

The optional `timestamp` field can be provided to indicate when the application was completed by the candidate, see [Sending signals] for details.

Follow the relevant application complete signal guidance for [Apply with SEEK] or [Ad Performance].

## Application status update for external applications

Once a candidate has completed the external apply form for a given position,
subsequent status updates represent the candidate’s progress through the recruitment process.

```mermaid Application status update for external applications
erDiagram

Position

Ad["SEEK job ad (Job Posting)"] {
  string positionProfileId
}

Position ||--o{ Ad : "advertised via"

Application }o--|| Position : "belongs to"

Application {
  ApplicationStatus status
}

SEEK["SEEK job application (external redirection)"] {
  string token
}

Signal["ApplicationStatusUpdate (Selection Signals)"] {
  string token
  string[] positionProfileIds
  SeekStatus status
}

Application ||--o| SEEK : "corresponds to"
Application ||--o{ Signal : sends
```

Pass the following [correlating identifiers]:

1. `seek-token` persisted from SEEK job ad or Apply with SEEK redirection

2. `positionProfileId`s of the SEEK job ads that correspond to the relevant position

   An array is accepted as the position may have been advertised on SEEK more than once.
   These identifiers are available in the [`PositionProfilePosted` event] from [Job Posting].

```graphql
mutation ($input: SendSignalInput!) {
  sendSignal(input: $input)
}
```

```jsonc default
{
  "input": {
    "typeCode": "ApplicationStatusUpdate",

    // The identifier to correlate the signal back to SEEK job ad or Apply with SEEK redirection.
    "token": "globalPublicTest-WfRFAhG25YC8ADe3QvxAVz",
    // The identifiers to correlate the signal back to SEEK job ads.
    "positionProfileIds": "seekAnzPublicTest:positionProfile:jobAd:27cuZeA47",

    "current": {
      // Omit `phase` if your software does not support hierarchies.
      "phase": {
        "label": "Hired",
        "index": 5,
        "total": 7
      },
      "status": {
        "label": "Hired",
        "index": 11,
        "total": 14
      },
      "success": true,
      "timestamp": "2020-01-02T00:00:00.000Z"
    },
    // Omit `previous` if your software does not track prior states.
    "previous": {
      "phase": {
        "label": "Offer",
        "index": 4,
        "total": 7
      },
      "status": {
        "label": "Offer accepted",
        "index": 8,
        "total": 14
      },
      "success": null,
      "timestamp": "2020-01-01T00:00:00.000Z"
    }
  }
}
```

## Application status update for Optimised Apply

Once your software has received a completed application from SEEK’s Apply Form,
subsequent status updates represent the candidate’s progress through the recruitment process.

```mermaid Application status update for Optimised Apply
erDiagram

Application {
  ApplicationStatus status
}

SEEK["SEEK job application (Optimised Apply)"] {
  string candidateApplicationProfileId
}

Signal["ApplicationStatusUpdate (Selection Signals)"] {
  string candidateApplicationProfileId
  SeekStatus status
}

Application ||--o| SEEK : "corresponds to"
Application ||--o{ Signal : sends
```

Pass the `candidateApplicationProfileId` of the SEEK job application as the [correlating identifier].

This identifier is available in the [`CandidateApplicationCreated` event] from [Optimised Apply].

```graphql
mutation ($input: SendSignalInput!) {
  sendSignal(input: $input)
}
```

```jsonc default
{
  "input": {
    "typeCode": "ApplicationStatusUpdate",

    // The identifier to correlate the signal back to the SEEK job application.
    "candidateApplicationProfileId": "seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT",

    "current": {
      // Omit `phase` if your software does not support hierarchies.
      "phase": {
        "label": "Hired",
        "index": 5,
        "total": 7
      },
      "status": {
        "label": "Hired",
        "index": 11,
        "total": 14
      },
      "success": true,
      "timestamp": "2020-01-02T00:00:00.000Z"
    },
    // Omit `previous` if your software does not track prior states.
    "previous": {
      "phase": {
        "label": "Offer",
        "index": 4,
        "total": 7
      },
      "status": {
        "label": "Offer accepted",
        "index": 8,
        "total": 14
      },
      "success": null,
      "timestamp": "2020-01-01T00:00:00.000Z"
    }
  }
}
```

[`CandidateApplicationCreated` event]: /use-cases/application-export#option-1-webhook
[`PositionProfilePosted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[Ad Performance]: /use-cases/ad-performance
[Apply with SEEK]: /use-cases/apply-with-seek
[correlating identifier]: /use-cases/selection-signals/data-structure#correlating-identifiers
[correlating identifiers]: /use-cases/selection-signals/data-structure#correlating-identifiers
[External apply Form]: /use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form
[Job Posting]: /use-cases/job-posting
[Optimised Apply]: /use-cases/application-export
[SEEK’s Apply Form]: /use-cases/job-posting/application-methods#seeks-apply-form
[Sending signals]: /use-cases/selection-signals/sending-signals
